import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, StatusPill } from "@/components/common";
import { PaginatedTable } from "@/components/PaginatedTable";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";

interface Dataset { id: string; name: string; size: number }
interface NamedRow { id: string; name: string }
interface Run {
  id: string; dataset: string; status: string;
  created_at: string; metrics?: Record<string, number>;
}

export function Evals() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();
  const [baseline, setBaseline] = useState<string>("");
  const [candidate, setCandidate] = useState<string>("");
  const [diff, setDiff] = useState<unknown>(null);

  // Run-config flow: an eval run targets a dataset AGAINST a specific target
  // (an agent or a retrieval profile), so "Run" opens a small picker rather
  // than firing blindly.
  const [runFor, setRunFor] = useState<Dataset | null>(null);
  const [targetKind, setTargetKind] = useState<"agent" | "retrieval_profile">("agent");
  const [targetId, setTargetId] = useState("");
  const [k, setK] = useState(5);
  const [grader, setGrader] = useState<"exact" | "contains" | "judge">("exact");

  const agents = useQuery<{ results: NamedRow[] }>({
    queryKey: ["evals-agents", ws],
    enabled: !!ws,
    queryFn: () => rawFetch(`/agents/agents/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
  });
  const profiles = useQuery<{ results: NamedRow[] }>({
    queryKey: ["evals-profiles", ws],
    enabled: !!ws,
    queryFn: () => rawFetch(`/retrieval/profiles/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
  });
  const targets = targetKind === "agent"
    ? (agents.data?.results ?? [])
    : (profiles.data?.results ?? []);

  const trigger = useMutation({
    mutationFn: () => {
      if (!runFor) throw new Error("Pick a dataset first.");
      if (!targetId) throw new Error("Pick a target to evaluate.");
      return rawFetch(`/evals/runs/trigger/`, {
        method: "POST",
        body: JSON.stringify({
          workspace: ws,
          dataset: runFor.id,
          target_kind: targetKind,
          target_id: targetId,
          config: { k, grader },
        }),
      });
    },
    onSuccess: () => {
      setRunFor(null);
      setTargetId("");
      qc.invalidateQueries({ queryKey: ["table", "/evals/runs/"] });
    },
  });

  function openRun(d: Dataset) {
    setRunFor(d);
    setTargetId("");
    trigger.reset();
  }

  const computeDiff = useMutation({
    mutationFn: () => rawFetch(`/evals/runs/${candidate}/diff/?baseline=${baseline}`),
    onSuccess: (data) => setDiff(data),
  });

  return (
    <>
      <PageHeader title="Evals & Monitoring"
                  subtitle="Golden datasets, runs, regression deltas."
                  help={
                    <>
                      <p>Pin a <strong>golden dataset</strong>, run an agent or retrieval profile against it, and compare scores to a baseline. Regression deltas gate risky changes before they ship.</p>
                    </>
                  }
                  learnMore={{ to: "/howto", hash: "evals", label: "How evals gate changes" }} />

      {/* Run configuration — choose what to evaluate the dataset against */}
      {runFor && (
        <div className="card mb-4 border-accent/40">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              Run eval on <code className="font-mono">{runFor.name}</code>
            </h2>
            <button className="btn-ghost text-xs" onClick={() => setRunFor(null)}>✕</button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_2fr_auto_auto]">
            <label className="flex flex-col gap-0.5 text-[11px]">
              <span className="uppercase tracking-wide text-muted">Target type</span>
              <select className="input" value={targetKind}
                      onChange={(e) => { setTargetKind(e.target.value as typeof targetKind); setTargetId(""); }}>
                <option value="agent">Agent</option>
                <option value="retrieval_profile">Retrieval profile</option>
              </select>
            </label>
            <label className="flex flex-col gap-0.5 text-[11px]">
              <span className="uppercase tracking-wide text-muted">Target</span>
              <select className="input" value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}>
                <option value="">Select a {targetKind === "agent" ? "agent" : "profile"}…</option>
                {targets.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-0.5 text-[11px]">
              <span className="uppercase tracking-wide text-muted">k</span>
              <input type="number" min={1} max={50} className="input w-16"
                     value={k} onChange={(e) => setK(Number(e.target.value) || 5)} />
            </label>
            <label className="flex flex-col gap-0.5 text-[11px]">
              <span className="uppercase tracking-wide text-muted">Grader</span>
              <select className="input" value={grader}
                      onChange={(e) => setGrader(e.target.value as typeof grader)}>
                <option value="exact">exact</option>
                <option value="contains">contains</option>
                <option value="judge">judge (LLM)</option>
              </select>
            </label>
          </div>
          {targets.length === 0 && (
            <p className="mt-2 text-[11px] text-muted">
              No {targetKind === "agent" ? "agents" : "retrieval profiles"} in this
              workspace yet — create one first.
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button className="btn-primary text-xs"
                    disabled={!targetId || trigger.isPending}
                    onClick={() => trigger.mutate()}>
              {trigger.isPending ? "Starting…" : "Start run"}
            </button>
            {trigger.isError && <ErrorBox error={trigger.error} />}
          </div>
        </div>
      )}

      <h2 className="mb-2 text-sm font-medium text-muted">Datasets</h2>
      <PaginatedTable<Dataset>
        endpoint="/evals/datasets/"
        baseParams={{ workspace: ws ?? undefined }}
        searchPlaceholder="Search datasets…"
        cols={[
          { header: "Name", cell: (d) => d.name },
          { header: "Size", cell: (d) => d.size },
          { header: "ID",   cell: (d) => <code className="font-mono text-xs">{shortId(d.id)}</code> },
          { header: "",     width: "1%", cell: (d) => (
              <button className="btn-primary" disabled={trigger.isPending}
                      onClick={() => openRun(d)}>Run</button>
            ),
          },
        ]}
      />

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Runs</h2>
      <PaginatedTable<Run>
        endpoint="/evals/runs/"
        baseParams={{ workspace: ws ?? undefined }}
        statusOptions={[
          { label: "Pending",   value: "pending"   },
          { label: "Running",   value: "running"   },
          { label: "Succeeded", value: "succeeded" },
          { label: "Failed",    value: "failed"    },
        ]}
        searchPlaceholder="Search by dataset / target…"
        refetchInterval={5000}
        cols={[
          { header: "ID",      cell: (r) => <code className="font-mono text-xs">{shortId(r.id)}</code> },
          { header: "Dataset", cell: (r) => <code className="font-mono text-xs">{shortId(r.dataset)}</code> },
          { header: "Status",  cell: (r) => <StatusPill status={r.status} /> },
          { header: "Created", cell: (r) => fmtDate(r.created_at) },
          { header: "Metrics", cell: (r) => (
              <code className="font-mono text-xs">
                {r.metrics ? JSON.stringify(r.metrics) : "—"}
              </code>
            ),
          },
        ]}
      />

      <div className="card mt-6">
        <div className="mb-2 text-xs uppercase text-muted">Regression delta</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]">
          <input className="input" placeholder="baseline run id"
                 value={baseline} onChange={(e) => setBaseline(e.target.value)} />
          <input className="input" placeholder="candidate run id"
                 value={candidate} onChange={(e) => setCandidate(e.target.value)} />
          <button className="btn-primary"
                  disabled={!baseline || !candidate || computeDiff.isPending}
                  onClick={() => computeDiff.mutate()}>Compute</button>
        </div>
        {computeDiff.isError && <div className="mt-2"><ErrorBox error={computeDiff.error} /></div>}
        {diff != null && (
          <pre className="mt-3 max-h-[260px] overflow-auto rounded border border-border bg-bg p-2 text-xs">
            {JSON.stringify(diff, null, 2)}
          </pre>
        )}
      </div>
    </>
  );
}
